"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import { useAuth } from "@/shared/lib/hooks/use-auth";
import { useDesktopToken } from "@/shared/lib/hooks/use-desktop-token";
import { useLcuStatus } from "@/shared/lib/hooks/use-lcu-status";
import { useMatchHostInfo } from "@/shared/lib/hooks/use-match-host-info";
import { checkIsTauri } from "@/shared/lib/tauri";
import { Badge } from "@/shared/ui/data-display/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { apiUrl } from "@/shared/api/config";
import { CreateLobbyButton } from "@/features/create-lobby/ui/create-lobby-button";
import type { GameflowPhase, WinConditionType } from "@/shared/types/tauri";

// Ключи Ui-словаря.
const WIN_CONDITION_LABELS: Record<WinConditionType, string> = {
  Kill: "litLcuStatusPanel.winKill",
  CreepScore100: "litLcuStatusPanel.winCreepScore100",
  TowerDestroyed: "litLcuStatusPanel.winTowerDestroyed",
};

const PHASE_LABELS: Record<GameflowPhase, string> = {
  None: "litLcuStatusPanel.phaseNone",
  Lobby: "litLcuStatusPanel.phaseLobby",
  Matchmaking: "litLcuStatusPanel.phaseMatchmaking",
  ReadyCheck: "litLcuStatusPanel.phaseReadyCheck",
  ChampSelect: "litLcuStatusPanel.phaseChampSelect",
  GameStart: "litLcuStatusPanel.phaseGameStart",
  InProgress: "litLcuStatusPanel.phaseInProgress",
  WaitingForStats: "litLcuStatusPanel.phaseWaitingForStats",
  PreEndOfGame: "litLcuStatusPanel.phasePreEndOfGame",
  EndOfGame: "litLcuStatusPanel.phaseEndOfGame",
  Reconnect: "litLcuStatusPanel.phaseReconnect",
};

const PHASE_BADGE_VARIANT: Record<GameflowPhase, string> = {
  None: "secondary",
  Lobby: "info",
  Matchmaking: "info",
  ReadyCheck: "warning",
  ChampSelect: "warning",
  GameStart: "warning",
  InProgress: "live",
  WaitingForStats: "info",
  PreEndOfGame: "info",
  EndOfGame: "success",
  Reconnect: "destructive",
};

interface LcuStatusPanelProps {
  tournamentId?: string;
  matchId?: string;
  bestOf?: number;
  tournamentType?: string;
  matchStatus?: string;
  teamAId?: string | null;
  teamBId?: string | null;
  className?: string;
}

export function LcuStatusPanel({
  tournamentId,
  matchId,
  bestOf = 1,
  tournamentType,
  matchStatus,
  teamAId,
  teamBId,
  className,
}: LcuStatusPanelProps) {
  const tUi = useTranslations("Ui");
  const {
    status,
    matchResult,
    liveGame,
    lastSubmitEvent,
    seriesScore,
    win1v1,
    setActiveMatch,
    clearActiveMatch,
    setActive1v1Match,
    clearActive1v1Match,
  } = useLcuStatus();
  const { user } = useAuth();
  const { token: desktopToken, getToken: getDesktopToken } = useDesktopToken();
  const { currentUserIsHost, currentUserIsParticipant } = useMatchHostInfo(matchId);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const is1v1 = tournamentType === "SoloOneVsOne";

  useEffect(() => {
    if (!tournamentId || !matchId || !user) return;

    let cancelled = false;
    const run = async () => {
      // JWT обязательно, иначе Tauri пошлёт Bearer="" → 401 → матч никогда
      // не advance'нется и условия победы 1v1 не сработают.
      const authToken = (await getDesktopToken()) ?? "";
      if (cancelled) return;

      const req = {
        matchId,
        tournamentId,
        authToken,
        backendUrl:
          typeof window !== "undefined"
            ? window.location.origin
            : apiUrl,
        bestOf,
      };

      if (is1v1) {
        setActive1v1Match(req);
      } else {
        setActiveMatch(req);
      }
    };
    void run();

    return () => {
      cancelled = true;
      if (is1v1) {
        clearActive1v1Match();
      } else {
        clearActiveMatch();
      }
    };
  }, [tournamentId, matchId, user, bestOf, is1v1, getDesktopToken, setActiveMatch, clearActiveMatch, setActive1v1Match, clearActive1v1Match]);

  // При периодическом обновлении JWT (раз в ~59 мин) пушим свежий токен в Tauri.
  useEffect(() => {
    if (!desktopToken || !tournamentId || !matchId || !user) return;
    const req = {
      matchId,
      tournamentId,
      authToken: desktopToken,
      backendUrl:
        typeof window !== "undefined"
          ? window.location.origin
          : apiUrl,
      bestOf,
    };
    if (is1v1) {
      setActive1v1Match(req);
    } else {
      setActiveMatch(req);
    }
  }, [desktopToken, tournamentId, matchId, user, bestOf, is1v1, setActive1v1Match, setActiveMatch]);

  useEffect(() => {
    if (lastSubmitEvent?.success) {
      setSubmitted(true);
      setError(null);
    } else if (lastSubmitEvent && !lastSubmitEvent.success) {
      setError(lastSubmitEvent.error ?? tUi("litLcuStatusPanel.oshibkaAvtomaticheskoyOtpravki"));
    }
  }, [lastSubmitEvent, tUi]);

  useEffect(() => {
    if (status?.phase === "InProgress" || status?.phase === "ChampSelect") {
      setSubmitted(false);
      setError(null);
    }
  }, [status?.phase]);

  const handleManualSubmit = useCallback(async () => {
    if (!tournamentId || !matchId || !matchResult || !user) return;

    setSubmitting(true);
    setError(null);

    try {
      const { invokeTauri } = await import("@/shared/lib/tauri");
      const authToken = (await getDesktopToken()) ?? "";
      if (!authToken) {
        setError(tUi("litLcuStatusPanel.neUdalosPoluchitToken"));
        setSubmitting(false);
        return;
      }

      const result = await invokeTauri<{ success: boolean; message: string }>(
        "submit_match_result",
        {
          req: {
            tournamentId,
            matchId,
            authToken,
            backendUrl:
              typeof window !== "undefined"
                ? window.location.origin
                : apiUrl,
          },
        },
      );

      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.message);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : tUi("litLcuStatusPanel.oshibkaOtpravki"));
    } finally {
      setSubmitting(false);
    }
  }, [tournamentId, matchId, matchResult, user, getDesktopToken, tUi]);

  if (!checkIsTauri() || !status) return null;

  const isSeries = bestOf > 1;
  const [wins, losses] = seriesScore;
  const winsNeeded = Math.ceil(bestOf / 2);
  const seriesComplete = wins >= winsNeeded || losses >= winsNeeded;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <span
            className={`h-2 w-2 rounded-full ${status.connected ? "bg-green-500" : "bg-red-500"}`}
          />
          League Client
          {status.connected && status.summonerName && (
            <span className="text-muted-foreground font-normal">
              — {status.summonerName}
            </span>
          )}
          {isSeries && (
            <Badge variant="outline" className="ml-auto">
              {wins}:{losses} Bo{bestOf}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!status.connected && (
          <p className="text-muted-foreground text-sm">
            {tUi("lcuStatusPanel.zapustiteLeagueOfLegends")}
          </p>
        )}

        {status.connected && (
          <>
            {matchId &&
              teamAId &&
              teamBId &&
              matchStatus &&
              (matchStatus === "Scheduled" || matchStatus === "Live") && (
                <CreateLobbyButton
                  matchId={matchId}
                  size="sm"
                  variant="outline"
                  fullWidth
                  isHost={currentUserIsHost}
                  disabledReason={
                    // Блокируем создание во время игры — LCU всё равно отдаст
                    // 400, а так юзер видит понятную причину сразу.
                    status?.phase === "ChampSelect" ||
                    status?.phase === "GameStart" ||
                    status?.phase === "InProgress" ||
                    status?.phase === "Matchmaking" ||
                    status?.phase === "ReadyCheck" ||
                    status?.phase === "WaitingForStats" ||
                    status?.phase === "PreEndOfGame" ||
                    status?.phase === "Reconnect"
                      ? tUi("litLcuStatusPanel.seychasZakonchi", { phase: tUi(PHASE_LABELS[status.phase]) })
                      : null
                  }
                />
              )}

            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">{tUi("lcuStatusPanel.faza")}</span>
              <Badge
                variant={
                  PHASE_BADGE_VARIANT[status.phase] as
                    | "default"
                    | "secondary"
                    | "destructive"
                    | "success"
                    | "live"
                    | "warning"
                    | "info"
                }
              >
                {tUi(PHASE_LABELS[status.phase])}
              </Badge>
            </div>

            {liveGame && status.phase === "InProgress" && (
              <div className="text-muted-foreground space-y-1 text-xs">
                <p>
                  {liveGame.gameMode} —{" "}
                  {Math.floor(liveGame.gameTime / 60)}:
                  {String(Math.floor(liveGame.gameTime % 60)).padStart(2, "0")}
                </p>
              </div>
            )}

            {/* KAN-14: matchResult — это локально отслеженная клиентом игра, не
                привязанная к открытому matchId. Показываем результат/кнопку
                подтверждения ТОЛЬКО участнику именно этого матча, иначе на чужом
                матче виден чужой результат и «Подтвердить». */}
            {matchResult && !submitted && currentUserIsParticipant && (
              <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-center gap-2">
                  <Badge variant={matchResult.win ? "success" : "destructive"}>
                    {matchResult.win ? tUi("litLcuStatusPanel.pobeda") : tUi("litLcuStatusPanel.porazhenie")}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    {matchResult.kda.kills}/{matchResult.kda.deaths}/
                    {matchResult.kda.assists} — {matchResult.championName}
                  </span>
                </div>

                {tournamentId && matchId && (
                  <Button
                    size="sm"
                    onClick={handleManualSubmit}
                    disabled={submitting}
                    className="w-full"
                  >
                    {submitting ? tUi("litLcuStatusPanel.otpravka") : tUi("litLcuStatusPanel.podtverditRezultat")}
                  </Button>
                )}
              </div>
            )}

            {submitted && !seriesComplete && (
              <p className="text-sm text-green-600 dark:text-green-400">
                Результат отправлен автоматически
                {isSeries && ` (${wins}:${losses})`}
              </p>
            )}

            {seriesComplete && (
              <div className="rounded-md border border-green-500 bg-green-50 p-3 dark:bg-green-950">
                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                  Серия завершена: {wins}:{losses}
                  {wins > losses ? tUi("litLcuStatusPanel.pobeda2") : tUi("litLcuStatusPanel.porazhenie2")}
                </p>
              </div>
            )}

            {win1v1 && (
              <div className="rounded-md border p-3 space-y-1">
                <p className="text-xs text-muted-foreground">{tUi("lcuStatusPanel.n1v1UsloviePobedy")}</p>
                <p className="text-sm font-medium">
                  {tUi(WIN_CONDITION_LABELS[win1v1.condition])}
                </p>
                <p className="text-xs text-muted-foreground">
                  Победитель: {win1v1.winnerSummoner}
                  {isSeries && tUi("litLcuStatusPanel.schetSerii", { wins, losses, bestOf })}
                </p>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
